import { Card } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { FileText, ExternalLink } from "lucide-react";

interface PBSDomain {
  name: string;
  color: string;
}

interface KnowledgeBase {
  title: string;
  url?: string;
}

interface CircularDomainDialProps {
  domains: PBSDomain[];
}

// Sample knowledge bases for each PBS domain - linked to Federal Marketplace solutions
const getKnowledgeBases = (domainName: string): KnowledgeBase[] => {
  const kbMap: Record<string, KnowledgeBase[]> = {
    "Design Automation": [
      { title: "Requirements Generation: Floor Plan & Space Requirements", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-002" },
      { title: "Design Tools: Timing & ROM Cost Generation", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-001" },
      { title: "Subject Matter Expertise Customization: Security & Historic Preservation", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-003" },
      { title: "Government Standards Integration Framework", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-014" },
      { title: "Site-Specific Constraints Management", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-015" }
    ],
    "Space and Furniture Layout": [
      { title: "Space Planning Standards and Guidelines", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-002" },
      { title: "Furniture Systems and Ergonomics", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-008" },
      { title: "Modular Workspace Design Templates", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-002" },
      { title: "ADA Compliance and Accessibility Standards", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-002" },
      { title: "Collaborative Space Configuration Tools", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-010" }
    ],
    "Analysis Tools": [
      { title: "Energy Performance Analysis Software", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-008" },
      { title: "Structural Load Calculation Tools", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-014" },
      { title: "Daylight and Solar Analysis", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-008" },
      { title: "HVAC System Sizing and Optimization", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-002" },
      { title: "Life Cycle Cost Analysis Framework", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-005" }
    ],
    "Cost and Schedule Estimation": [
      { title: "ROM Cost Estimating Database", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-001" },
      { title: "Schedule Development Templates", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-001" },
      { title: "Value Engineering Methodologies", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-011" },
      { title: "Construction Cost Indices and Trends", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-001" },
      { title: "Project Risk and Contingency Analysis", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-005" }
    ],
    "Visualization & 3D Rendering": [
      { title: "BIM Modeling Standards and Protocols", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-002" },
      { title: "Virtual Reality Walkthroughs", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-010" },
      { title: "3D Rendering and Animation Tools", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-010" },
      { title: "Digital Twin Development", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-010" },
      { title: "Stakeholder Presentation Templates", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-002" }
    ],
    "Post-Occupancy Monitoring": [
      { title: "Building Performance Metrics Dashboard", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-008" },
      { title: "Occupant Satisfaction Surveys", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-007" },
      { title: "Energy Consumption Monitoring", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-008" },
      { title: "Space Utilization Analytics", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-002" },
      { title: "Maintenance and Operations Feedback Loop", url: "/channel-solutions?channel=gsa&demo=true&id=gsa-008" }
    ]
  };
  
  return kbMap[domainName] || [];
};

const CircularDomainDial = ({ domains }: CircularDomainDialProps) => {
  return (
    <Card className="p-6 bg-white shadow-sm">
      <h3 className="text-lg font-semibold mb-4">GSA Public Buildings Service (PBS) Domain Elements</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {domains.map((domain, index) => {
          const knowledgeBases = getKnowledgeBases(domain.name);
          
          return (
            <HoverCard key={index} openDelay={200} closeDelay={100}>
              <HoverCardTrigger asChild>
                <div
                  className={`${domain.color} text-white p-4 rounded-lg text-center text-sm font-medium cursor-pointer transition-all hover:scale-105 hover:shadow-lg`}
                >
                  {domain.name}
                </div>
              </HoverCardTrigger>
              <HoverCardContent 
                side="bottom" 
                align="start"
                className="w-80 bg-white p-4 shadow-xl border border-gray-200 z-50"
              >
                <div className="space-y-1">
                  <h4 className="font-semibold text-sm mb-3">{domain.name}</h4>
                  <div className="space-y-2">
                    {knowledgeBases.map((kb, kbIndex) => (
                      <a
                        key={kbIndex}
                        href={kb.url}
                        className="flex items-start gap-2 p-2 rounded hover:bg-gray-50 transition-colors group"
                      >
                        <FileText className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-xs flex-1 text-gray-700 group-hover:text-blue-600">
                          {kb.title}
                        </span>
                        <ExternalLink className="h-3 w-3 text-gray-400 flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                    ))}
                  </div>
                </div>
              </HoverCardContent>
            </HoverCard>
          );
        })}
      </div>
    </Card>
  );
};

export default CircularDomainDial;
